import type { ReactNode, ButtonHTMLAttributes } from 'react';
import './Button.css';

type ButtonVariant = 'primary' | 'secondary' | 'ghost';
type ButtonSize = 'sm' | 'md' | 'lg';

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  /** Visual style variant */
  variant?: ButtonVariant;
  /** Size preset */
  size?: ButtonSize;
  /** Optional icon to display before text */
  icon?: ReactNode;
  /** Whether the button should take full width */
  fullWidth?: boolean;
  /** External link href - renders as anchor if provided */
  href?: string;
  /** Button content */
  children: ReactNode;
}

/**
 * Button component with multiple variants and sizes.
 * Renders as anchor tag when href is provided.
 */
export function Button({
  variant = 'primary',
  size = 'md',
  icon,
  fullWidth = false,
  href,
  children,
  className = '',
  ...props
}: ButtonProps) {
  const classes = ['btn', `btn-${variant}`, `btn-${size}`, fullWidth && 'btn-full', className]
    .filter(Boolean)
    .join(' ');

  const content = (
    <>
      {icon && <span className="btn-icon">{icon}</span>}
      <span className="btn-text">{children}</span>
    </>
  );

  if (href) {
    return (
      <a href={href} className={classes} target="_blank" rel="noopener noreferrer">
        {content}
      </a>
    );
  }

  return (
    <button className={classes} {...props}>
      {content}
    </button>
  );
}
